import React, { useEffect, useState, useMemo } from 'react';
import { Rocket, Clock, Calendar, Users, Eye, Trash2, Loader2, History, X } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const BulkPublishTab = ({ activeDrivers }) => {
  const [form, setForm] = useState({
    date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10), // tomorrow by default
    trips_per_driver: 4,
    start_hour: 7,
    end_hour: 22,
    interval_min: 30,
    seats_per_trip: 4,
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [history, setHistory] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const { toast } = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const totalPlanned = useMemo(() => activeDrivers.length * form.trips_per_driver, [activeDrivers.length, form.trips_per_driver]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/admin/bulk-publish/history');
      setHistory(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadHistory(); }, []);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post('/admin/bulk-publish', { ...form, dry_run: true });
      setPreview(data);
    } catch (e) {
      toast({ title: 'Erro no preview', description: apiError(e), variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  };

  const runPublish = async () => {
    const ok = window.confirm(
      `Publicar ${totalPlanned} viagens para ${activeDrivers.length} motoristas no dia ${form.date.split('-').reverse().join('/')}?`
    );
    if (!ok) return;
    setPublishing(true);
    setPreview(null);
    try {
      const { data } = await api.post('/admin/bulk-publish', { ...form, dry_run: false });
      toast({
        title: 'Viagens publicadas!',
        description: `${data.total_trips_created} viagens em ${data.total_drivers} motoristas${data.skipped_past > 0 ? ` · ${data.skipped_past} ignoradas (horário no passado)` : ''}.`,
      });
      loadHistory();
    } catch (e) {
      toast({ title: 'Falha ao publicar', description: apiError(e), variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  };

  const deleteBatch = async (batch) => {
    const ok = window.confirm(
      `EXCLUIR ${batch.total_trips_created} viagens publicadas em ${batch.date}? Reservas confirmadas serão canceladas.`
    );
    if (!ok) return;
    setDeleting(batch.id);
    try {
      const { data } = await api.delete(`/admin/bulk-publish/${batch.id}`);
      toast({
        title: 'Lote excluído',
        description: `${data.trips_removed} viagens removidas${data.reservations_cancelled > 0 ? ` · ${data.reservations_cancelled} reservas canceladas` : ''}.`,
        variant: 'destructive',
      });
      loadHistory();
    } catch (e) {
      toast({ title: 'Erro ao excluir', description: apiError(e), variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const timeSlots = useMemo(() => {
    const slots = [];
    const total = (form.end_hour - form.start_hour) * 60;
    for (let m = 0; m <= total; m += form.interval_min) {
      const h = form.start_hour + Math.floor(m / 60);
      const mm = m % 60;
      slots.push(`${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`);
    }
    return slots;
  }, [form.start_hour, form.end_hour, form.interval_min]);

  return (
    <div className="space-y-4">
      {/* KPI panel */}
      <div
        className="rounded-2xl p-4 text-white"
        style={{ background: 'linear-gradient(135deg, #071e42 0%, #0B2A5B 50%, #123b7a 100%)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Rocket size={20} className="text-[#F5C518]" />
          <div className="font-extrabold text-lg">Publicação Automática</div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-white/10 rounded-xl p-2">
            <div className="text-[10px] uppercase font-bold text-white/70">Motoristas</div>
            <div className="text-2xl font-extrabold" data-testid="bulk-drivers-count">{activeDrivers.length}</div>
          </div>
          <div className="bg-white/10 rounded-xl p-2">
            <div className="text-[10px] uppercase font-bold text-white/70">Por motorista</div>
            <div className="text-2xl font-extrabold">{form.trips_per_driver}</div>
          </div>
          <div className="bg-white/10 rounded-xl p-2" style={{ background: 'rgba(245,197,24,0.2)' }}>
            <div className="text-[10px] uppercase font-bold text-white/70">Total</div>
            <div className="text-2xl font-extrabold" style={{ color: '#F5C518' }} data-testid="bulk-total-planned">{totalPlanned}</div>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-white/70 text-center">
          Horário {String(form.start_hour).padStart(2,'0')}:00 → {String(form.end_hour).padStart(2,'0')}:00 · {timeSlots.length} horários disponíveis · intervalo {form.interval_min}min
        </div>
      </div>

      {/* Config form */}
      <div className="bg-white rounded-2xl p-4 border border-[var(--bj-border)] space-y-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-[var(--bj-muted)] mb-1 flex items-center gap-1"><Calendar size={13} /> Data das viagens</label>
          <input type="date" className="round-input" value={form.date} onChange={(e) => set('date', e.target.value)} data-testid="bulk-date" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-[var(--bj-muted)] mb-1">Viagens por motorista</label>
            <input type="number" min="1" max="10" className="round-input" value={form.trips_per_driver} onChange={(e) => set('trips_per_driver', Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-[var(--bj-muted)] mb-1">Vagas por viagem</label>
            <input type="number" min="1" max="8" className="round-input" value={form.seats_per_trip} onChange={(e) => set('seats_per_trip', Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-[var(--bj-muted)] mb-1">Hora inicial</label>
            <input type="number" min="0" max="23" className="round-input" value={form.start_hour} onChange={(e) => set('start_hour', Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-[var(--bj-muted)] mb-1">Hora final</label>
            <input type="number" min="1" max="23" className="round-input" value={form.end_hour} onChange={(e) => set('end_hour', Number(e.target.value))} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-[var(--bj-muted)] mb-1">Intervalo entre horários (minutos)</label>
          <select className="round-input" value={form.interval_min} onChange={(e) => set('interval_min', Number(e.target.value))}>
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={45}>45 min</option>
            <option value={60}>60 min</option>
          </select>
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={runPreview}
          disabled={previewing || publishing}
          className="chip justify-center py-3 text-sm font-bold disabled:opacity-60"
          data-testid="bulk-preview-btn"
        >
          {previewing ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
          Visualizar
        </button>
        <button
          onClick={runPublish}
          disabled={publishing || previewing || activeDrivers.length === 0}
          className="justify-center py-3 text-sm font-extrabold text-white rounded-full flex items-center gap-2 disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #2B5FCC 0%, #0B2A5B 100%)', boxShadow: '0 4px 14px rgba(11,42,91,0.3)' }}
          data-testid="bulk-publish-btn"
        >
          {publishing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
          PUBLICAR TODAS
        </button>
      </div>

      {/* Preview panel */}
      {preview && (
        <div className="bg-white rounded-2xl p-4 border border-[var(--bj-border)]">
          <div className="flex items-center justify-between mb-2">
            <div className="font-extrabold text-[var(--bj-text)] flex items-center gap-2">
              <Eye size={16} /> Pré-visualização
            </div>
            <button onClick={() => setPreview(null)} className="w-7 h-7 rounded-full hover:bg-[var(--bj-cream-2)] flex items-center justify-center">
              <X size={14} />
            </button>
          </div>
          <div className="text-sm text-[var(--bj-text)] mb-2">
            {preview.total_trips_planned} viagens serão criadas para {preview.total_drivers} motoristas.
            {preview.skipped_past > 0 && <span className="text-[var(--bj-red)]"> {preview.skipped_past} serão ignoradas (horário já passou).</span>}
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1 text-xs">
            {preview.preview.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-[var(--bj-border)] last:border-0">
                <span className="font-semibold text-[var(--bj-text)] truncate flex-1 mr-2">{p.driver_name}</span>
                <span className="text-[var(--bj-muted)] shrink-0">{p.time} · {p.origin} → {p.destination} · R${p.price.toFixed(2).replace('.', ',')}</span>
              </div>
            ))}
          </div>
          {preview.total_trips_planned > preview.preview.length && (
            <div className="mt-2 text-[11px] text-[var(--bj-muted)] text-center">+ {preview.total_trips_planned - preview.preview.length} viagens não mostradas</div>
          )}
        </div>
      )}

      {/* History */}
      <div className="bg-white rounded-2xl p-4 border border-[var(--bj-border)]">
        <div className="flex items-center gap-2 mb-3 font-extrabold text-[var(--bj-text)]">
          <History size={16} /> Histórico de publicações em lote
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-[var(--bj-muted)] text-center py-4">Nenhuma publicação em lote ainda.</div>
        ) : (
          <div className="space-y-2">
            {history.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 border border-[var(--bj-border)] rounded-xl p-3" data-testid={`history-${b.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[var(--bj-text)] text-sm">
                    {b.total_trips_created} viagens · {b.date}
                  </div>
                  <div className="text-[11px] text-[var(--bj-muted)]">
                    {b.total_drivers} motoristas · {b.trips_per_driver}/motorista · {String(b.start_hour).padStart(2,'0')}:00–{String(b.end_hour).padStart(2,'0')}:00
                  </div>
                  <div className="text-[11px] text-[var(--bj-muted)]">por {b.admin_name} · {new Date(b.created_at).toLocaleString('pt-BR')}</div>
                </div>
                <button
                  onClick={() => deleteBatch(b)}
                  disabled={deleting === b.id}
                  className="btn-outline-danger text-xs justify-center px-3 py-2 shrink-0 disabled:opacity-50"
                  data-testid={`delete-batch-${b.id}`}
                >
                  {deleting === b.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  Excluir
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkPublishTab;
